import { create } from 'zustand';
import { supabase } from '../lib/supabase';

export interface FontAlias {
  id: string;
  originalCss: string;
  assignedFamily: string;
  assignedWeight: number | null;
  assignedStyle: string | null;
  usageCount: number;
  updatedAt: string;
}

interface FontAliasStoreState {
  aliases: FontAlias[];
  isLoading: boolean;
  isLoaded: boolean;
  lastError: string | null;

  ensureLoaded: () => Promise<void>;
  refresh: () => Promise<void>;
  upsertAlias: (payload: {
    originalCss: string;
    assignedFamily: string;
    assignedWeight: number | null;
    assignedStyle: string | null;
  }) => Promise<{ ok: boolean; error?: string }>;
  deleteAlias: (originalCss: string) => Promise<{ ok: boolean; error?: string }>;
}

function mapRow(row: Record<string, unknown>): FontAlias {
  return {
    id: (row.id as string) ?? '',
    originalCss: (row.original_css as string) ?? '',
    assignedFamily: (row.assigned_family as string) ?? '',
    assignedWeight: (row.assigned_weight as number | null) ?? null,
    assignedStyle: (row.assigned_style as string | null) ?? null,
    usageCount: (row.usage_count as number | null) ?? 0,
    updatedAt: (row.updated_at as string) ?? '',
  };
}

export const useFontAliasStore = create<FontAliasStoreState>((set, get) => ({
  aliases: [],
  isLoading: false,
  isLoaded: false,
  lastError: null,

  ensureLoaded: async () => {
    if (get().isLoaded || get().isLoading) return;
    await get().refresh();
  },

  refresh: async () => {
    set({ isLoading: true, lastError: null });
    const { data, error } = await supabase
      .from('cs_svg_font_aliases')
      .select('id, original_css, assigned_family, assigned_weight, assigned_style, usage_count, updated_at')
      .order('original_css', { ascending: true });
    if (error) {
      set({ isLoading: false, lastError: error.message });
      return;
    }
    set({
      aliases: (data ?? []).map(mapRow),
      isLoading: false,
      isLoaded: true,
      lastError: null,
    });
  },

  upsertAlias: async ({ originalCss, assignedFamily, assignedWeight, assignedStyle }) => {
    const trimmedOriginal = originalCss.trim();
    const trimmedFamily = assignedFamily.trim();
    if (!trimmedOriginal) return { ok: false, error: 'original_css is required' };
    if (!trimmedFamily) return { ok: false, error: 'assigned_family is required' };

    const { error } = await supabase
      .from('cs_svg_font_aliases')
      .upsert(
        {
          original_css: trimmedOriginal,
          assigned_family: trimmedFamily,
          assigned_weight: assignedWeight,
          assigned_style: assignedStyle ?? 'normal',
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'original_css' }
      );
    if (error) return { ok: false, error: error.message };
    await get().refresh();
    return { ok: true };
  },

  deleteAlias: async (originalCss) => {
    const { error } = await supabase
      .from('cs_svg_font_aliases')
      .delete()
      .eq('original_css', originalCss);
    if (error) return { ok: false, error: error.message };
    await get().refresh();
    return { ok: true };
  },
}));
